import React, { HTMLAttributes, useCallback, useEffect, useState, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import { Context } from "@xt/xtbase";
const { XtContext } = Context;
import store from "store";
import Storage from "utils/storage";
import { getKlineAxisPricescale } from "utils/tradingViewKlineAxisPricescale";
import { addKlineChannel } from "utils/socket/klineChannel";
import { get_cfd_kline, get_kline } from "api/v4/market";
import { widget, ResolutionString } from "@/components/Tradingview/v22/charting_library";

import tvConfig, { getTimezone, getTradingViewCustomCssUrl } from "@/components/Tradingview/v22/config";
import styles from "./index.module.scss";

/**
 * K 线时间轴与休市空白说明（TradingView）
 * ----------------------------------------
 * - 当前：session = "24x7"，无休市模式。
 * - 股票模式（休市不产生空白）：见下方「接口/Socket 对接说明」中「若后续支持股票模式」。
 *
 * 接口 / Socket 对接说明
 * ----------------------------------------
 * 【历史 K 线】GET /sapi/v4/market/public/kline
 *   - 请求 params：symbol（如 btc_usdt_cfd）, interval（1m|5m|15m|30m|1h|2h|4h|6h|8h|12h|1d|3d|1w|1M）, endTime(ms, 可选), limit(≤1000), version=2
 *   - 响应：数组，每项 { t, o, c, h, l, q, v }，t 为开盘时间毫秒，o/h/l/c 为字符串价格，q 成交量，v 成交额
 *   - 前端会 unshift 成「时间正序」再交给 TradingView，且 bar.time 使用 t（毫秒）
 *
 * 【实时 K 线】WebSocket subscribe
 *   - 订阅：params: ["kline@{symbol},{interval}"]，如 kline@btc_usdt_cfd,5m
 *   - 推送单条 K 线：与历史接口同结构 { t, o, c, h, l, q, v }，前端 tvDataFormat 后 onRealtimeCallback
 *
 * 是否需要接口改造？
 *   - 当前 24x7 模式：不需要。接口按「时间连续」返回即可，无数据时段前端会留白。
 *   - 若后续支持股票模式（session 0930-1600 等）：仍可不改接口。后端若只存/返「交易时段内」的 K 线，
 *     且 bar 的 t 为该 bar 在交易所时区下的开始时间（再转 UTC 毫秒），则前端只需在 resolveSymbol 里
 *     配 session + timezone，即可无空白连续展示。若后端目前是 24 小时连续写入，则需后端在「股票品种」
 *     上只落盘 09:30-16:00 的 bar，或由前端按 session 过滤（不推荐，费流量）。
 */
// ---------- 股票 session / Mock 调试代码（调试完成后注释，当前使用无休市 24x7） ----------
// const KLINE_SESSION_STOCK = "0930-1600";
// const KLINE_TIMEZONE_STOCK = "America/New_York";
// const KLINE_SESSION_MOCK_KEY = "kline_session_stock_mock";
// function getKlineSessionStockMock(): boolean {
//   if (typeof window === "undefined") return false;
//   const url = new URLSearchParams(window.location.search);
//   if (url.get("kline_session") === "stock") return true;
//   if (url.get("kline_session") === "24x7") return false;
//   return window.localStorage?.getItem(KLINE_SESSION_MOCK_KEY) === "1";
// }
// function setKlineSessionStockMock(useStock: boolean) {
//   if (typeof window === "undefined") return;
//   if (useStock) window.localStorage?.setItem(KLINE_SESSION_MOCK_KEY, "1");
//   else window.localStorage?.removeItem(KLINE_SESSION_MOCK_KEY);
//   window.location.reload();
// }
// const KLINE_MOCK_DATA_KEY = "kline_mock_stock_data";
// function getKlineMockDataEnabled(): boolean {
//   if (typeof window === "undefined") return false;
//   const url = new URLSearchParams(window.location.search);
//   if (url.get("kline_mock") === "1") return true;
//   return window.localStorage?.getItem(KLINE_MOCK_DATA_KEY) === "1";
// }
// function setKlineMockDataEnabled(enabled: boolean) {
//   if (typeof window === "undefined") return;
//   if (enabled) window.localStorage?.setItem(KLINE_MOCK_DATA_KEY, "1");
//   else window.localStorage?.removeItem(KLINE_MOCK_DATA_KEY);
//   window.location.reload();
// }
// const STOCK_SESSION_START_UTC_HOUR = 14;
// const STOCK_SESSION_START_UTC_MIN = 30;
// const STOCK_SESSION_END_UTC_HOUR = 21;
// const STOCK_SESSION_END_UTC_MIN = 0;
// function generateMockStockKlineBars(resolution: string, toSeconds: number, countBack: number) {
//   const toMs = toSeconds * 1000;
//   const intervalMinutes: Record<string, number> = { "1": 1, "3": 3, "5": 5, "15": 15, "30": 30, "60": 60, "120": 120, "240": 240, "1D": 24 * 60, "1W": 7 * 24 * 60, "1M": 30 * 24 * 60 };
//   const stepMs = (intervalMinutes[resolution] || 5) * 60 * 1000;
//   const bars: { time: number; open: number; high: number; low: number; close: number; volume: number }[] = [];
//   let basePrice = 100;
//   const dayMs = 24 * 60 * 60 * 1000;
//   const toDate = new Date(toMs);
//   const curDate = new Date(toDate.getUTCFullYear(), toDate.getUTCMonth(), toDate.getUTCDate(), 0, 0, 0, 0);
//   const isWeekday = (d: Date) => { const day = d.getUTCDay(); return day >= 1 && day <= 5; };
//   const getSessionStart = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 14, 30, 0, 0);
//   const getSessionEnd = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 21, 0, 0, 0);
//   let totalBars = 0;
//   const maxBars = Math.min(countBack, 1500);
//   const isDaily = resolution === "1D" || resolution === "1W" || resolution === "1M";
//   if (isDaily) {
//     for (let dayOffset = 0; totalBars < maxBars && dayOffset < 400; dayOffset++) {
//       const d = new Date(curDate.getTime() - dayOffset * dayMs);
//       if (!isWeekday(d)) continue;
//       const t = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0);
//       if (t > toMs) continue;
//       const open = basePrice; const change = (Math.random() - 0.48) * 2; const close = open + change;
//       bars.unshift({ time: t, open: Math.round(open * 100) / 100, high: Math.round((Math.max(open, close) + Math.random() * 0.8) * 100) / 100, low: Math.round((Math.min(open, close) - Math.random() * 0.8) * 100) / 100, close: Math.round(close * 100) / 100, volume: Math.floor(100000 + Math.random() * 200000) });
//       basePrice = close; totalBars++;
//     }
//   } else {
//     for (let dayOffset = 0; totalBars < maxBars && dayOffset < 120; dayOffset++) {
//       const d = new Date(curDate.getTime() - dayOffset * dayMs);
//       if (!isWeekday(d)) continue;
//       const sessionEnd = getSessionEnd(d); const sessionStart = getSessionStart(d);
//       for (let t = sessionEnd - stepMs; t >= sessionStart && totalBars < maxBars; t -= stepMs) {
//         if (t > toMs) continue;
//         const open = basePrice; const change = (Math.random() - 0.48) * 1.2; const close = open + change;
//         bars.unshift({ time: t, open: Math.round(open * 100) / 100, high: Math.round((Math.max(open, close) + Math.random() * 0.4) * 100) / 100, low: Math.round((Math.min(open, close) - Math.random() * 0.4) * 100) / 100, close: Math.round(close * 100) / 100, volume: Math.floor(10000 + Math.random() * 50000) });
//         basePrice = close; totalBars++;
//       }
//     }
//   }
//   return bars;
// }
// ---------- 股票 session / Mock 调试代码结束 ----------

const TvIntervalToXtApi = {
  "1": "1m",
  "3": "3m",
  "5": "5m",
  "15": "15m",
  "30": "30m",
  "60": "1h",
  "120": "2h",
  "240": "4h",
  "360": "6h",
  "480": "8h",
  "720": "12h",
  "1D": "1d",
  "3D": "3d",
  "1W": "1w",
  "1M": "1M",
}; //tradingView内部周期对应xt接口请求参数，key: tv内部周期, value: 请求参数

import XtLoading from "@/components/Xt/Loading";

interface KlineProps {
  s?: string; //symbol 交易对
  i?: string; //interval 周期间隔
  t: number; //开盘时间(time)，毫秒
  o: string; //开盘价(open)
  c: string; //收盘价(close)
  h: string; //最高价(high)
  l: string; //最低价(low)
  q: string; //成交量(quantity)
  v: string; //成交额(volume)
}

import { OptionTradingViewProps, getOptionTradingView, ActionTradingViewProps } from "../../index";

/** Convert /buy Time 分时面积图 — Figma 13999-57581 */
const CONVERT_TIME_AREA_LINE_COLOR = "#B9F78A";
const CONVERT_TIME_AREA_FILL_TOP = "rgba(185, 247, 138, 0.2)";
const CONVERT_TIME_AREA_FILL_BOTTOM = "rgba(185, 247, 138, 0)";

interface Props extends HTMLAttributes<HTMLDivElement> {
  option: WithUndefined<OptionTradingViewProps>;
  action: WithUndefined<ActionTradingViewProps>;
  isStandard?: boolean;
  loadingClassName?: string;
  /** Convert 等场景使用现货 K 线（symbol / WS 均不带 _cfd） */
  useSpotKline?: boolean;
  /** 仅展示价格线，不加载指标（MA/Volume 等）与全局 tvChart 布局 */
  priceLineOnly?: boolean;
  /** 显式 K 线 symbol（Convert 页传入），避免依赖 store.market.name 更新时序 */
  chartSymbol?: string;
}

const Main: React.FC<Props> = ({ option, action, isStandard, className, loadingClassName, useSpotKline, priceLineOnly, chartSymbol }) => {
  const [appState] = React.useContext(XtContext);
  const { isH5 } = store.app;
  const { name, formatName } = store.market;
  const activeSymbol = chartSymbol || name;
  const activeSymbolRef = useRef(activeSymbol);

  useEffect(() => {
    activeSymbolRef.current = activeSymbol;
  }, [activeSymbol]);

  const [loading, setLoading] = useState<boolean>(true);
  const [tvWidget, setTvWidget] = useState<any>();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<any>(null);
  const isMountedRef = useRef(true);
  const lastBarTimeRef = useRef<Record<string, number>>({});
  const subscriptionEventRef = useRef<Record<string, { event: string; unsub?: () => void }>>({});

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 获取K线颜色方案
  const klineColorScheme = useMemo(() => {
    const savedScheme = Storage.get("klineColorScheme") as "green_up" | "red_up";
    return savedScheme === "red_up" || savedScheme === "green_up" ? savedScheme : "green_up";
  }, []);

  const buildTvOverrides = useCallback(
    (theme: string, scheme: "green_up" | "red_up" = klineColorScheme) => {
      const overrides = tvConfig({ theme, klineColorScheme: scheme, standardStyle: isStandard }).overrides;
      const { chartType } = getOptionTradingView(option);
      if (priceLineOnly && chartType === 3) {
        return {
          ...overrides,
          "mainSeriesProperties.areaStyle.linecolor": CONVERT_TIME_AREA_LINE_COLOR,
          "mainSeriesProperties.areaStyle.color1": CONVERT_TIME_AREA_FILL_TOP,
          "mainSeriesProperties.areaStyle.color2": CONVERT_TIME_AREA_FILL_BOTTOM,
        };
      }
      return overrides;
    },
    [option, priceLineOnly, klineColorScheme, isStandard]
  );

  const themeChange = useCallback(
    (theme) => {
      if (!tvWidget) return;
      tvWidget.changeTheme(theme === "dark" ? "Dark" : "Light");
      setTimeout(() => {
        // 这里只同步 TradingView 原生主题/十字线样式，不额外叠加自定义价格浮层。
        tvWidget.applyOverrides(buildTvOverrides(theme));
      }, 0);
    },
    [tvWidget, buildTvOverrides]
  );
  const onChartDataLoaded = useCallback(() => {
    console.log("【tradingView】new symbol's data has been received.");
  }, []);
  // 双保险移除 Volume：除了禁用默认创建外，saved_data 恢复旧布局时
  // 仍可能把 Volume study 带回来，因此在图表 ready 后再主动清一次。
  const removeVolumeStudy = useCallback((widgetInstance: any) => {
    try {
      if (!widgetInstance?.chart || typeof widgetInstance.chart !== "function") return;
      const chart = widgetInstance.chart();
      if (!chart?.getAllStudies || !chart?.removeEntity) return;
      chart.getAllStudies().forEach((study) => {
        if (study?.name === "Volume") {
          chart.removeEntity(study.id);
        }
      });
    } catch (err) {
      console.warn("【TradingView】remove volume study error", err);
    }
  }, []);
  const removeAllStudies = useCallback((widgetInstance: any) => {
    try {
      if (!widgetInstance?.chart || typeof widgetInstance.chart !== "function") return;
      const chart = widgetInstance.chart();
      if (!chart?.getAllStudies || !chart?.removeEntity) return;
      chart.getAllStudies().forEach((study) => {
        if (study?.id != null) {
          chart.removeEntity(study.id);
        }
      });
    } catch (err) {
      console.warn("【TradingView】remove all studies error", err);
    }
  }, []);
  const clearStudies = useCallback(
    (widgetInstance: any) => {
      if (priceLineOnly) {
        removeAllStudies(widgetInstance);
      } else {
        removeVolumeStudy(widgetInstance);
      }
    },
    [priceLineOnly, removeAllStudies, removeVolumeStudy]
  );
  const tvDataFormat = useCallback((obj: KlineProps) => {
    return {
      time: obj.t,
      open: +obj.o,
      high: +obj.h,
      low: +obj.l,
      close: +obj.c,
      volume: +obj.q,
      volume2: +obj.v,
    };
  }, []);

  const toKlineApiSymbol = useCallback(
    (symbol: string) => {
      const raw = symbol || "";
      if (useSpotKline) return raw.replace(/_cfd$/i, "");
      if (raw.endsWith("_cfd")) return raw;
      return `${raw}_cfd`;
    },
    [useSpotKline]
  );

  const getBars = useCallback((symbolInfo, resolution, periodParams, onHistoryCallback) => {
    const { to, countBack, firstDataRequest } = periodParams;
    // if (getKlineMockDataEnabled()) { const mockBars = generateMockStockKlineBars(...); onHistoryCallback(...); return; }
    const symbol = toKlineApiSymbol(symbolInfo.ticker);
    const interval = TvIntervalToXtApi[resolution];

    get_kline({
      params: {
        symbol: symbol,
        interval,
        endTime: firstDataRequest ? undefined : to * 1000,
        limit: Math.min(countBack, 1000),
        version: 2,
      },
    }).then((data: KlineProps[]) => {
      const ary: any = [];
      const cacheKey = `${symbolInfo.ticker}_${resolution}`;
      if (data && data.length) {
        data.map((obj) => {
          ary.unshift(tvDataFormat(obj));
        });
        const latestBar = ary[ary.length - 1];
        if (latestBar?.time) {
          lastBarTimeRef.current[cacheKey] = latestBar.time;
        }
        // 供开仓校验用：socket 从未推送时，用 kline 最新一根的时间作为「价格更新时间」
        if (symbol === store.market.name) {
          const latestBarTime = Math.max(...data.map((d) => d.t));
          store.cfdView.updateState({ lastKlineBarTime: latestBarTime });
        }
      } else {
        delete lastBarTimeRef.current[cacheKey];
      }
      onHistoryCallback(ary, { noData: !ary.length });
    });

    // CFD 接口调用（暂时注释，后续可能再切换）
    // const currentSymbol = store.market.symbols?.find((s: any) => s.symbol === symbol);
    // const symbolId = currentSymbol?.id;
    // 
    // if (!symbolId) {
    //   console.error("【CFD K线】未找到交易对的 symbolId:", symbol);
    //   onHistoryCallback([], { noData: true });
    //   return;
    // }
    // 
    // get_cfd_kline({
    //   params: {
    //     symbolId,
    //     interval,
    //     endTime: firstDataRequest ? undefined : to * 1000,
    //     limit: Math.min(countBack, 1000),
    //   },
    // }).then((response: any) => {
    //   const data = response?.data || response || [];
    //   const ary: any = [];
    //   if (data && data.length) {
    //     data.map((obj) => {
    //       ary.unshift(tvDataFormat(obj));
    //     });
    //   }
    //   onHistoryCallback(ary, { noData: !ary.length });
    // }).catch((err) => {
    //   console.error("【CFD K线接口错误】", err);
    //   onHistoryCallback([], { noData: true });
    // });
  }, [tvDataFormat, toKlineApiSymbol, useSpotKline]);
  const subscribeBars = useCallback((symbolInfo, resolution, onRealtimeCallback, listenerGuid) => {
    // console.log("【TradingView】------->[subscribeBars]", { symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback });
    const symbol = toKlineApiSymbol(symbolInfo?.ticker || activeSymbolRef.current);
    const interval = TvIntervalToXtApi[resolution];
    const cacheKey = `${symbolInfo.ticker}_${resolution}`;
    const msg = {
      event: `kline@${symbol},${interval}`,
    };
    if (listenerGuid) {
      subscriptionEventRef.current[listenerGuid]?.unsub?.();
      subscriptionEventRef.current[listenerGuid] = {
        event: msg.event,
        unsub: addKlineChannel(msg.event, (data: KlineProps) => {
          const nextBar = tvDataFormat(data);
          const lastBarTime = lastBarTimeRef.current[cacheKey] || 0;
          if (nextBar?.time && lastBarTime && nextBar.time < lastBarTime) {
            return;
          }
          if (nextBar?.time) {
            lastBarTimeRef.current[cacheKey] = nextBar.time;
            if (symbol === activeSymbolRef.current) {
              store.cfdView.updateState({ lastKlineBarTime: nextBar.time });
            }
          }
          onRealtimeCallback(nextBar);
        }),
      };
    }
  }, [tvDataFormat, toKlineApiSymbol]);
  const unsubscribeBars = useCallback((listenerGuid) => {
    // console.log("【TradingView】------->[unsubscribeBars]", listenerGuid);
    const subscription = subscriptionEventRef.current[listenerGuid];
    if (!subscription) return;
    subscription.unsub?.();
    delete subscriptionEventRef.current[listenerGuid];
  }, []);

  //TradingView init
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const chartLocalConfig = priceLineOnly ? undefined : Storage.get("tvChart");
    const tvCfg = tvConfig({ theme: appState.theme, klineColorScheme, standardStyle: isStandard });
    const { interval } = getOptionTradingView(option);
    const tvConstructor = new widget({
      ...tvCfg,
      custom_css_url: getTradingViewCustomCssUrl(),
      disabled_features: tvCfg.disabled_features.concat(isH5 ? ["widget_logo"] : []),
      container,
      symbol: formatName(activeSymbol),
      locale: (() => {
        const obj = {
          "zh-CN": "zh",
          "zh-HK": "zh_TW",
          en: "en",
          ja: "ja",
          ko: "ko",
          ru: "ru",
          es: "es",
          tr: "tr",
        };
        return obj[appState.lang] || "en";
      })(),
      interval,
      saved_data: chartLocalConfig,
      datafeed: {
        onReady(callback) {
          // console.log("【TradingView】------->[onReady]");
          return new Promise((resolve) => {
            resolve({
              supported_resolutions: Object.keys(TvIntervalToXtApi),
            });
          }).then((data) => callback(data));
        },
        //通过商品名称解析商品信息
        resolveSymbol(symbolName, onSymbolResolvedCallback, onResolveErrorCallback) {
          return new Promise((resolve) => {
            const sym = activeSymbolRef.current || store.market.name;
            const { currentConfig, formatName } = store.market;
            const resolvedName = formatName(sym);
            const tickerSymbol = useSpotKline
              ? toKlineApiSymbol(sym)
              : sym && !sym.endsWith("_cfd")
                ? `${sym}_cfd`
                : sym;
            resolve({
              name: resolvedName,
              full_name: resolvedName,
              ticker: tickerSymbol,
              minmov: 1,
              pricescale: getKlineAxisPricescale(currentConfig?.pricePrecision ?? 2),
              timezone: getTimezone(),
              has_intraday: true,
              has_daily: true,
              has_weekly_and_monthly: true,
              session: "24x7",
              volume_precision: 2,
              exchange: "Spikex",
            });
          })
            .then((data) => onSymbolResolvedCallback(data))
            .catch((err) => onResolveErrorCallback(err));
        },
        //当图表库需要由日期范围定义的历史K线片段时，将调用此函数。
        getBars,
        //订阅K线数据。图表库将调用onRealtimeCallback方法以更新实时数据。
        subscribeBars,
        //取消订阅K线数据
        unsubscribeBars,
      },
    } as any);

    widgetRef.current = tvConstructor;

    tvConstructor.onChartReady(() => {
      if (!isMountedRef.current) {
        try {
          tvConstructor.remove();
        } catch (e) {
          console.warn("【TradingView】widget.remove on stale ready", e);
        }
        return;
      }
      setTvWidget(tvConstructor);
    });

    // 币种切换时因 key 变化会卸载本组件，需销毁 widget 避免残留
    return () => {
      Object.keys(subscriptionEventRef.current).forEach((listenerGuid) => {
        unsubscribeBars(listenerGuid);
      });
      subscriptionEventRef.current = {};
      const widgetInstance = widgetRef.current;
      widgetRef.current = null;
      if (widgetInstance && typeof widgetInstance.remove === "function") {
        try {
          widgetInstance.remove();
        } catch (e) {
          console.warn("【TradingView】widget.remove on unmount", e);
        }
      }
      if (isMountedRef.current) {
        setTvWidget(undefined);
      }
    };
  }, [priceLineOnly, unsubscribeBars]);
  //TradingView ready
  useEffect(() => {
    if (!tvWidget) return;

    try {
      const { theme } = appState;
      if (tvWidget.getTheme && typeof tvWidget.getTheme === "function") {
        const currentTheme = tvWidget.getTheme();
        if (currentTheme && currentTheme.toLowerCase() !== theme) {
          themeChange(theme);
        }
      }

      const chartLocalConfig = priceLineOnly ? undefined : Storage.get("tvChart");
      const tvOverrides = buildTvOverrides(theme);

      if (!priceLineOnly && tvWidget.subscribe && typeof tvWidget.subscribe === "function") {
        tvWidget.subscribe("undo_redo_state_changed", () => {
          // console.log("undo_redo_state_changed-=====");
          if (tvWidget && tvWidget.save) {
            tvWidget.save((state) => {
              Storage.set("tvChart", state);
            });
          }
        });
      }

      if (chartLocalConfig) {
        if (tvWidget.applyOverrides && typeof tvWidget.applyOverrides === "function") {
          tvWidget.applyOverrides(tvOverrides);
        }
        clearStudies(tvWidget);
      } else if (!priceLineOnly) {
        if (tvWidget.chart && typeof tvWidget.chart === "function") {
          (() => {
            try {
              clearStudies(tvWidget);
              tvWidget.chart().createStudy(
                "Moving Average",
                false,
                false,
                { length: 5 },
                {
                  "plot.color": "#0075FF",
                  "plot.linewidth": 1,
                }
              );
              tvWidget.chart().createStudy(
                "Moving Average",
                false,
                false,
                { length: 15 },
                {
                  "plot.color": "#5C00F3",
                  "plot.linewidth": 1,
                }
              );
              tvWidget.chart().createStudy(
                "Moving Average",
                false,
                false,
                { length: 30 },
                {
                  "plot.color": "#FFC453",
                  "plot.linewidth": 1,
                }
              );

              setTimeout(() => {
                if (tvWidget && tvWidget.save) {
                  console.log("tvWidget.save");
                  tvWidget.save((state) => {
                    Storage.set("tvChart", state);
                  });
                }
              }, 0);
            } catch (err) {
              console.error("【TradingView】createStudy error", err);
            }
          })();
        }
      } else {
        clearStudies(tvWidget);
        if (tvWidget.applyOverrides && typeof tvWidget.applyOverrides === "function") {
          tvWidget.applyOverrides(tvOverrides);
        }
      }

      const { interval, chartType } = getOptionTradingView(option);
      if (tvWidget.chart && typeof tvWidget.chart === "function") {
        tvWidget.chart().setChartType(chartType);
        clearStudies(tvWidget);
        if (tvWidget.applyOverrides && typeof tvWidget.applyOverrides === "function") {
          tvWidget.applyOverrides(buildTvOverrides(theme));
        }
      }

      if (tvWidget.activeChart && typeof tvWidget.activeChart === "function") {
        tvWidget.activeChart().executeActionById("chartReset");
      }

      if (tvWidget.setSymbol && typeof tvWidget.setSymbol === "function") {
        tvWidget.setSymbol(activeSymbol, interval as ResolutionString, onChartDataLoaded);
      }

      setLoading(false);
    } catch (err) {
      console.error("【TradingView】ready error", err);
    }
  }, [tvWidget, clearStudies, priceLineOnly, buildTvOverrides, option, appState.theme, activeSymbol, onChartDataLoaded]);

  useEffect(() => {
    // console.log("【tradingView】useEffect====", name, option, tvWidget);
    if (!tvWidget || !activeSymbol) return;
    // 差价合约通过 key={name} 在币种切换时整组件重挂载，此处处理 symbol / 周期 / 类型切换
    try {
      const { interval, chartType } = getOptionTradingView(option);
      tvWidget.setSymbol(activeSymbol, interval as ResolutionString, onChartDataLoaded);
      if (tvWidget.chart && typeof tvWidget.chart === "function") {
        tvWidget.chart().setChartType(chartType);
      }
      if (priceLineOnly) {
        clearStudies(tvWidget);
        tvWidget.applyOverrides(buildTvOverrides(appState.theme));
      }
    } catch (err) {
      console.warn("【TradingView】symbol/option change error", err);
    }
  }, [tvWidget, activeSymbol, option, priceLineOnly, clearStudies, buildTvOverrides, appState.theme, onChartDataLoaded]);
  useEffect(() => {
    // console.log("appState.theme=",appState.theme);
    if (!tvWidget) return;
    const { theme } = appState;
    themeChange(theme);
  }, [appState.theme, themeChange]);

  // 监听K线颜色方案变化（通过 storage 事件）
  useEffect(() => {
    if (!tvWidget) return;
    
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === Storage.keyList.klineColorScheme) {
        const savedScheme = Storage.get("klineColorScheme") as "green_up" | "red_up";
        const currentScheme = savedScheme === "red_up" || savedScheme === "green_up" ? savedScheme : "green_up";
        const { theme } = appState;
        setTimeout(() => {
          tvWidget.applyOverrides(buildTvOverrides(theme, currentScheme));
        }, 0);
      }
    };
    
    window.addEventListener("storage", handleStorageChange);
    
    // 也监听同标签页内的变化（通过自定义事件）
    const handleCustomStorageChange = () => {
      const savedScheme = Storage.get("klineColorScheme") as "green_up" | "red_up";
      const currentScheme = savedScheme === "red_up" || savedScheme === "green_up" ? savedScheme : "green_up";
      const { theme } = appState;
      setTimeout(() => {
        tvWidget.applyOverrides(buildTvOverrides(theme, currentScheme));
      }, 0);
    };
    
    window.addEventListener("klineColorSchemeChange", handleCustomStorageChange);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("klineColorSchemeChange", handleCustomStorageChange);
    };
  }, [tvWidget, appState.theme, buildTvOverrides]);
  useEffect(() => {
    if (!tvWidget || !action) return;
    const { actionId } = action;
    actionId && tvWidget.chart().executeActionById(actionId);
  }, [action]);

  return (
    <div className={cx(styles.main, { [styles.standard]: isStandard }, className)}>
      {/* 股票 session / Mock 调试 UI（调试完成后注释）
      <div className={styles.mockSessionWrap}>
        <span className={styles.mockSessionLabel}>K线时间轴 Mock：</span>
        <button type="button" className={styles.mockSessionBtn} onClick={() => setKlineSessionStockMock(!getKlineSessionStockMock())}>...</button>
        <span className={styles.mockSessionLabel}>Mock 数据：</span>
        <button type="button" className={styles.mockSessionBtn} onClick={() => setKlineMockDataEnabled(!getKlineMockDataEnabled())}>...</button>
      </div>
      */}
      <div ref={containerRef} className={styles.tv}></div>
      {loading && <XtLoading className={cx(styles.loading, loadingClassName)} />}
    </div>
  );
};

export default observer(Main);
